import type { TranscriptLine } from "@scoach/types";
import { create } from "zustand";

export interface ChecklistEntry {
  id: string;
  text: string;
  asked: boolean;
  origin: "master" | "manual" | "ai";
}

interface InterviewLiveState {
  meetingId: string | null;
  transcript: TranscriptLine[];
  livePartial: string | null;
  listening: boolean;
  muted: boolean;
  startedAt: number | null;
  checklist: ChecklistEntry[];
  generatedQuestions: string[];

  setMeetingId: (id: string) => void;
  setStartedAt: (t: number) => void;
  setListening: (v: boolean) => void;
  setMuted: (v: boolean) => void;
  setLivePartial: (t: string | null) => void;
  addTranscriptLine: (line: TranscriptLine) => void;
  setChecklist: (items: ChecklistEntry[]) => void;
  toggleAsked: (id: string) => void;
  addGeneratedQuestions: (qs: string[]) => void;
  addChecklistItem: (text: string, origin: ChecklistEntry["origin"]) => void;
  reset: () => void;
}

export const useInterviewLiveStore = create<InterviewLiveState>((set) => ({
  meetingId: null,
  transcript: [],
  livePartial: null,
  listening: false,
  muted: false,
  startedAt: null,
  checklist: [],
  generatedQuestions: [],

  setMeetingId: (meetingId) => set({ meetingId }),
  setStartedAt: (startedAt) => set({ startedAt }),
  setListening: (listening) => set({ listening }),
  setMuted: (muted) => set({ muted }),
  setLivePartial: (livePartial) => set({ livePartial }),
  addTranscriptLine: (line) =>
    set((s) => {
      if (s.transcript.some((l) => l.id === line.id)) return s;
      return { transcript: [...s.transcript, line] };
    }),
  setChecklist: (checklist) => set({ checklist }),
  toggleAsked: (id) =>
    set((s) => ({
      checklist: s.checklist.map((c) => (c.id === id ? { ...c, asked: !c.asked } : c)),
    })),
  addGeneratedQuestions: (qs) =>
    set((s) => ({ generatedQuestions: [...s.generatedQuestions, ...qs] })),
  addChecklistItem: (text, origin) =>
    set((s) => ({
      checklist: [
        ...s.checklist,
        { id: `${origin}-${crypto.randomUUID()}`, text, asked: false, origin },
      ],
    })),
  reset: () =>
    set({
      transcript: [],
      livePartial: null,
      listening: false,
      muted: false,
      startedAt: null,
      checklist: [],
      generatedQuestions: [],
    }),
}));
